import { createFileRoute, useParams } from "@tanstack/react-router";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";
import Left from "@repo/icons/default/Left";
import { ContactsHistoryIdAvatar } from "@/components/assist/assist-contacts-history/contacts-history-id-avatar";
import { ContactsHistoryIdHeaderActions } from "@/components/assist/assist-contacts-history/contacts-history-id-header-actions";
import { ContactsHistoryIdHeaderPrice } from "@/components/assist/assist-contacts-history/contacts-history-id-header-price";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { AssistFeatures } from "@/enums/assist-features";
import { useGraphQL } from "@/hooks/use-graphql";
import { validateFeatureAccess } from "@/lib/assist-feature-guard";
import { ServicePackageEnum } from "@/lib/enums/service-package-enum";
import { ContactsHistoryIdDetails } from "@/components/assist/assist-contacts-history/contacts-history-id-details";
import { ContactsHistoryIdBillingDetails } from "@/components/assist/assist-contacts-history/contacts-history-id-billing-details";

import { FIVE_MINUTES_STALE_TIME } from "@/lib/constants/dashboards-customer-overview";

import type { JSX } from "react";

const ContactsHistoryIdPageQuery = graphql(`
  query ContactsHistoryIdPageQuery($id: Int!, $codPacoteServicos: Int!) {
    nuntius_contato_by_pk(id: $id) {
      ...ContactsHistoryIdHeaderActionsFragment
      ...ContactsHistoryIdHeaderAvatarFragment
      ...ContactsHistoryIdDetailsFragment
    }
    ...ContactsHistoryIdHeaderPriceFragment
    ...ContactsHistoryIdBillingDetailsFragment
  }
`);

const ContactsHistoryIdPage = (): JSX.Element => {
  const { contactId } = useParams({
    from: "/settings/assist/contacts-history/$contactId/",
  });

  const queryResult = useGraphQL(
    ContactsHistoryIdPageQuery,
    {
      id: contactId,
      codPacoteServicos: ServicePackageEnum.Pro.valueOf(),
    },
    {
      staleTime: FIVE_MINUTES_STALE_TIME,
    },
  );

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Detalhes" />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data?.nuntius_contato_by_pk ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between items-center">
                <ContactsHistoryIdHeaderPrice data={data} />
                <ContactsHistoryIdHeaderActions data={data.nuntius_contato_by_pk} />
              </div>
              <ContactsHistoryIdAvatar data={data.nuntius_contato_by_pk} />

              <div className="mt-6 flex flex-col gap-6">
                <ContactsHistoryIdDetails data={data.nuntius_contato_by_pk} />
                <ContactsHistoryIdBillingDetails data={data} />
              </div>
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/assist/contacts-history/$contactId/")({
  component: ContactsHistoryIdPage,
  parseParams: (params) => ({
    contactId: z.int().parse(Number(params.contactId)),
  }),
  beforeLoad: ({ context, location }) => {
    const { user } = context;
    validateFeatureAccess({
      location,
      user,
      codFuncionalidade: AssistFeatures.ContactsHistory,
    });
  },
});
